import { useState } from 'react';
import { Link } from 'react-router-dom';
import { articles, channels, hotList } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import { VerifiedBadge } from '@/components/feature/ArticleBits';
import { formatNum } from '@/components/feature/ArticleBits';

export default function FeedSection() {
  const [active, setActive] = useState<string>('all');
  const filtered = active === 'all' ? articles : articles.filter((a) => a.channel === active);
  const activeChannel = channels.find((c) => c.key === active);

  return (
    <section id="feed" className="scroll-mt-28 bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        {/* 板块标题 */}
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary-600">
              <i className="ri-flashlight-line" aria-hidden="true"></i>
              实时爆料流
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">
              {activeChannel?.name === '全部' ? '最新一手动态' : activeChannel?.name}
            </h2>
            <p className="mt-1.5 text-sm text-foreground-600">{activeChannel?.desc}</p>
          </div>
          <Link
            to="/about#contact"
            className="inline-flex w-fit items-center gap-1.5 rounded-md border border-background-300 px-3.5 py-2 text-xs font-medium whitespace-nowrap text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
          >
            <i className="ri-add-circle-line text-primary-600" aria-hidden="true"></i>
            提供线索 / 投稿
          </Link>
        </div>

        {/* 栏目 Tab */}
        <div className="mt-6 flex flex-wrap gap-2">
          {channels.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(c.key)}
              aria-pressed={active === c.key}
              className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                active === c.key
                  ? 'bg-primary-600 font-semibold text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-8 grid gap-10 lg:grid-cols-12">
          {/* 文章列表 */}
          <div className="lg:col-span-8">
            <div className="flex flex-col divide-y divide-background-200 border-b border-background-200 pb-2">
              {filtered.map((a) => (
                <div key={a.id} className="py-3">
                  <ArticleCard article={a} layout="list" />
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-center gap-2 rounded-md border border-dashed border-background-300 py-4 text-xs text-foreground-500">
              <i className="ri-loader-3-line text-primary-600" aria-hidden="true"></i>
              已加载 {filtered.length} 条 · 更多一手动态每日更新，欢迎持续关注
            </div>
          </div>

          {/* 热榜侧栏 */}
          <aside className="lg:col-span-4" aria-label="热门榜单">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
              <h3 className="flex items-center gap-2 font-display text-lg font-bold text-foreground-950">
                <i className="ri-fire-fill text-primary-600" aria-hidden="true"></i>
                实时热榜
              </h3>
              <ol className="mt-5 flex flex-col gap-1">
                {hotList.map((item) => {
                  const deltaIcon =
                    item.delta === 'up'
                      ? { cls: 'text-primary-600', icon: 'ri-arrow-up-line' }
                      : item.delta === 'down'
                        ? { cls: 'text-secondary-600', icon: 'ri-arrow-down-line' }
                        : item.delta === 'new'
                          ? { cls: 'text-accent-600', icon: 'ri-sparkling-line' }
                          : { cls: 'text-foreground-400', icon: 'ri-subtract-line' };
                  return (
                    <li key={item.id}>
                      <Link
                        to={`/article/${item.id}`}
                        className="group flex items-start gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-background-50"
                      >
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-sm font-display text-sm font-bold ${
                            item.rank <= 3 ? 'bg-primary-600 text-background-50' : 'bg-background-200 text-foreground-700'
                          }`}
                        >
                          {item.rank}
                        </span>
                        <span className="flex-1">
                          <span className="line-clamp-2 text-sm font-medium leading-6 text-foreground-800 transition-colors group-hover:text-primary-700">
                            {item.title}
                          </span>
                          <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line" aria-hidden="true"></i>
                              {item.heat}度
                            </span>
                            <span className={`flex items-center gap-0.5 ${deltaIcon.cls}`}>
                              <i className={deltaIcon.icon} aria-hidden="true"></i>
                              {item.delta === 'new' ? '新上榜' : item.delta === 'up' ? '上升' : item.delta === 'down' ? '下降' : '持平'}
                            </span>
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </div>

            {/* 侧栏说明卡 */}
            <div className="mt-5 rounded-lg border border-accent-200 bg-accent-50/60 p-5">
              <div className="flex items-center gap-2">
                <VerifiedBadge status="verified" />
                <h4 className="text-sm font-bold text-foreground-950">分级核实说明</h4>
              </div>
              <p className="mt-2.5 text-xs leading-6 text-foreground-700">
                每条爆料都会标注「已核实 / 待核实 / 持续更新」。热榜热度由阅读、评论与投稿同源数综合计算，仅作趋势参考，不代表事实定论。
              </p>
            </div>

            {/* 侧栏贡献榜 */}
            <div className="mt-5 rounded-lg border border-background-200 bg-background-100/60 p-5">
              <h4 className="flex items-center gap-2 font-display text-base font-bold text-foreground-950">
                <i className="ri-award-fill text-accent-600" aria-hidden="true"></i>
                本期贡献投稿人
              </h4>
              <ul className="mt-4 flex flex-col gap-3 text-sm">
                {['离职三月的阿凯 · 组织调整核实', '做背调的林姐 · 竞业拆解', '打烊的店长助理 · 一线排班'].map((s) => (
                  <li key={s} className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className="ri-shield-user-line" aria-hidden="true"></i>
                    </span>
                    <span className="line-clamp-1 text-xs text-foreground-700">{s}</span>
                    <i className="ri-medal-fill ml-auto text-accent-500" aria-hidden="true"></i>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[11px] leading-5 text-foreground-500">
                感谢各位的信任，平台正在核实 {formatNum(8)} 条新增线索。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}